import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./WalletInboxActionCenter.css";

type ActionItem={
  actionKey:string;
  actionType:string;
  priority:"HIGH"|"NORMAL";
  title:string;
  body?:string|null;
  actionPath:string;
  dueAt?:string|null;
  occurredAt?:string|null;
  sourceId:string;
  sourceStatus:string;
};

type NotificationItem={
  notificationId:string;
  severity:"NORMAL"|"HIGH";
  category:string;
  title:string;
  body?:string|null;
  actionPath?:string|null;
  status:"UNREAD"|"READ";
  sourceActive:boolean;
  firstSeenAt:string;
  lastSeenAt:string;
  readAt?:string|null;
};

type ActionCenterData={
  learnerId:string;
  counts:{
    actions:number;
    highPriorityActions:number;
    unreadNotifications:number;
  };
  actions:ActionItem[];
  notifications:NotificationItem[];
  generatedAt:string;
};

function formatDate(value?:string|null){
  if(!value)return "";
  const d=new Date(value);
  if(Number.isNaN(d.getTime()))return "";
  return d.toLocaleDateString("ko-KR",{year:"numeric",month:"2-digit",day:"2-digit"});
}

function actionTypeLabel(value:string){
  const map:Record<string,string>={
    ASSESSMENT:"검사",
    EXPERIENCE_REVISION:"경험 보완",
    EVIDENCE_REVISION:"증빙 보완",
    NEXT_ACTION:"다음 행동",
    GOAL_DUE:"목표",
    SHARE_EXPIRY:"공유"
  };
  return map[value] || value;
}

function statusHint(item:ActionItem){
  if(item.actionType==="ASSESSMENT"){
    return item.sourceStatus==="IN_PROGRESS" ? "진행 중" : "시작 전";
  }
  if(item.actionType==="EXPERIENCE_REVISION" || item.actionType==="EVIDENCE_REVISION"){
    return "보완 필요";
  }
  if(item.actionType==="SHARE_EXPIRY"){
    return "만료 예정";
  }
  if(item.dueAt){
    const due=new Date(item.dueAt);
    const now=new Date();
    const today=new Date(now.getFullYear(),now.getMonth(),now.getDate()).getTime();
    const dueDay=new Date(due.getFullYear(),due.getMonth(),due.getDate()).getTime();
    if(dueDay<today)return "기한 지남";
    if(dueDay===today)return "오늘";
  }
  return item.sourceStatus;
}

export function WalletInboxActionCenter({
  client,
  onNavigate
}:{
  client:PlatformClient;
  onNavigate:(path:string)=>void;
}){
  const [data,setData]=useState<ActionCenterData|null>(null);
  const [error,setError]=useState("");
  const [busy,setBusy]=useState("");
  const [filter,setFilter]=useState<"ALL"|"HIGH">("ALL");

  async function load(){
    const {data,error}=await client.rpc("wallet_action_center");
    if(error){setError(error.message);return;}
    setData(data as ActionCenterData);
    setError("");
  }

  useEffect(()=>{void load();},[client]);

  const actions=useMemo(()=>{
    if(!data)return [];
    return filter==="HIGH" ? data.actions.filter((item)=>item.priority==="HIGH") : data.actions;
  },[data,filter]);

  async function setNotificationState(notificationId:string,action:"READ"|"UNREAD"|"DISMISS"){
    setBusy(notificationId+":"+action);setError("");
    const {error}=await client.rpc("wallet_set_notification_state",{
      p_notification_id:notificationId,
      p_action:action
    });
    if(error){setError(error.message);setBusy("");return;}
    await load();
    setBusy("");
  }

  if(error && !data)return <p className="error">{error}</p>;
  if(!data)return <p className="muted">할 일과 알림을 정리하는 중…</p>;

  return (
    <section className="wallet-page wallet-inbox-page">
      <div className="wallet-inbox-hero">
        <div>
          <p className="eyebrow">INBOX & ACTION CENTER</p>
          <h2>지금 해야 할 일을 한곳에서 봐요</h2>
          <p>
            검사, 경험·증빙 보완, 목표와 다음 행동, 공유 만료처럼 지금 확인할 일을
            현재 기록 상태에서 자동으로 모아줍니다.
          </p>
        </div>

        <div className="wallet-inbox-summary">
          <div>
            <span>할 일</span>
            <strong>{data.counts.actions}</strong>
          </div>
          <div>
            <span>우선 확인</span>
            <strong>{data.counts.highPriorityActions}</strong>
          </div>
          <div>
            <span>읽지 않은 알림</span>
            <strong>{data.counts.unreadNotifications}</strong>
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      <section className="wallet-action-section">
        <header>
          <div>
            <p className="eyebrow">ACTION</p>
            <h3>해야 할 일</h3>
          </div>
          <div className="wallet-action-filter">
            <button className={filter==="ALL"?"active":""} onClick={()=>setFilter("ALL")}>전체</button>
            <button className={filter==="HIGH"?"active":""} onClick={()=>setFilter("HIGH")}>우선 확인</button>
          </div>
        </header>

        {actions.length ? (
          <div className="wallet-action-list">
            {actions.map((item)=>(
              <article className={item.priority==="HIGH"?"wallet-action-card high":"wallet-action-card"} key={item.actionKey}>
                <button className="wallet-action-open" onClick={()=>onNavigate(item.actionPath)}>
                  <div className="wallet-action-card-head">
                    <span>{actionTypeLabel(item.actionType)}</span>
                    <em>{statusHint(item)}</em>
                  </div>
                  <strong>{item.title}</strong>
                  {item.body && <p>{item.body}</p>}
                  <div className="wallet-action-card-meta">
                    {item.priority==="HIGH" && <span>우선 확인</span>}
                    {item.dueAt && <span>기한 {formatDate(item.dueAt)}</span>}
                    {!item.dueAt && item.occurredAt && <span>{formatDate(item.occurredAt)}</span>}
                  </div>
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="wallet-inbox-empty">
            <strong>{filter==="HIGH" ? "우선 확인할 일이 없습니다." : "지금 처리할 일이 없습니다."}</strong>
            <p>새로운 검사 배정이나 보완 요청, 일정이 생기면 이곳에 자동으로 나타납니다.</p>
          </div>
        )}
      </section>

      <section className="wallet-notification-section">
        <header>
          <div>
            <p className="eyebrow">INBOX</p>
            <h3>최근 알림</h3>
          </div>
          <span>{data.notifications.length}건</span>
        </header>

        {data.notifications.length ? (
          <div className="wallet-notification-list">
            {data.notifications.map((item)=>(
              <article
                className={[
                  "wallet-notification-card",
                  item.status==="UNREAD"?"unread":"",
                  item.severity==="HIGH"?"high":""
                ].filter(Boolean).join(" ")}
                key={item.notificationId}
              >
                <div className="wallet-notification-copy">
                  <div>
                    <span>{item.category}</span>
                    {item.status==="UNREAD" && <em>새 알림</em>}
                  </div>
                  <strong>{item.title}</strong>
                  {item.body && <p>{item.body}</p>}
                  <small>{formatDate(item.lastSeenAt)}</small>
                </div>

                <div className="wallet-notification-actions">
                  {item.actionPath && (
                    <button onClick={()=>onNavigate(item.actionPath!)}>열기</button>
                  )}
                  {item.status==="UNREAD" ? (
                    <button
                      disabled={busy!==""}
                      onClick={()=>void setNotificationState(item.notificationId,"READ")}
                    >
                      읽음
                    </button>
                  ) : (
                    <button
                      disabled={busy!==""}
                      onClick={()=>void setNotificationState(item.notificationId,"UNREAD")}
                    >
                      안읽음
                    </button>
                  )}
                  <button
                    disabled={busy!==""}
                    onClick={()=>void setNotificationState(item.notificationId,"DISMISS")}
                  >
                    닫기
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="wallet-inbox-empty compact">
            <strong>최근 Wallet 알림이 없습니다.</strong>
            <p>학생 Wallet 경로와 연결된 시스템 알림만 이곳에 표시합니다.</p>
          </div>
        )}
      </section>

      <div className="wallet-inbox-trust">
        <strong>할 일은 자동으로 갱신됩니다</strong>
        <span>
          여기서 ‘읽음’을 눌러도 검사나 보완 작업 자체가 완료되는 것은 아닙니다.
          실제 원본 기록 상태가 바뀌어야 할 일이 사라집니다.
        </span>
      </div>
    </section>
  );
}
